<template>
  <div class="toolbar">
    <div class="toolbar-left">
      <my-pagination :currentPage="currentPage" :totalPages="totalPages" @pageChanged="$emit('pageChanged', $event)" />
      <span class="total">Всего: <strong>{{ totalItems }}</strong></span>
    </div>
    <div class="toolbar-right">
      <button class="toolbar-btn" :disabled="refreshing" @click="$emit('refresh')">
        {{ refreshing ? 'Обновляем…' : 'Обновить' }}
      </button>
      <button class="toolbar-btn" :disabled="exporting" @click="$emit('export')">
        {{ exporting ? 'Готовим…' : 'Excel' }}
      </button>
      <button class="toolbar-btn settings-toggle" :class="{ active: settingsOpen }" :aria-expanded="settingsOpen" aria-controls="downtimes-table-settings" @click="settingsOpen = !settingsOpen">
        Настройки таблицы <span class="chevron" :class="{ open: settingsOpen }" aria-hidden="true" />
      </button>
      <button class="toolbar-btn" :class="{ active: filterOpen }" @click="$emit('toggleFilter')">
        {{ filterOpen ? 'Скрыть фильтры' : 'Фильтры' }}
      </button>
    </div>

    <transition name="settings-accordion">
      <div v-if="settingsOpen" id="downtimes-table-settings" class="settings-panel">
        <div class="settings-heading">
          <div>
            <strong>Столбцы таблицы</strong>
            <div class="settings-hint">Меняйте порядок, видимость и ширину столбцов.</div>
          </div>
          <div class="settings-actions">
            <button class="toolbar-btn" @click="$emit('resetSettings')">Сбросить</button>
            <button class="toolbar-btn" @click="settingsOpen = false">Готово</button>
          </div>
        </div>
        <TableScaleControl :modelValue="scale" @update:modelValue="$emit('scaleChanged', $event)" />
        <div class="hl-settings-row">
          <label class="settings-label">Подсветка</label>
          <select class="hl-select" :value="highlightMode" @change="$emit('highlightChanged', { highlightMode: $event.target.value })">
            <option v-for="mode in highlightModes" :key="mode.value" :value="mode.value">{{ mode.label }}</option>
          </select>
          <select class="hl-select" :value="palette" @change="$emit('highlightChanged', { highlightPalette: $event.target.value })" v-if="highlightMode !== 'none' && highlightMode !== 'classification' && highlightMode !== 'work_status'">
            <option v-for="p in palettes" :key="p.value" :value="p.value">{{ p.label }}</option>
          </select>
        </div>
        <draggable v-model="settingsOrder" class="settings-grid" item-key="id" handle=".drag-handle" @change="emitOrder">
          <template #item="{ element, index }">
            <div class="settings-row">
              <button class="drag-handle" type="button" :aria-label="`Перетащить столбец ${element.label}`" title="Перетащить"><span class="drag-grip" aria-hidden="true" /></button>
              <label class="visibility-label">
                <input type="checkbox" :checked="isVisible(element.id)" :disabled="isLastVisible(element.id)" @change="toggleVisibility(element.id)">
                <span>{{ element.label }}</span>
              </label>
              <span class="sort-indicator">{{ sortIndicator(element.id) }}</span>
               <button type="button" class="move-btn" :disabled="index === 0" aria-label="Выше" @click="move(index, -1)">↑</button>
               <button type="button" class="move-btn" :disabled="index === settingsOrder.length - 1" aria-label="Ниже" @click="move(index, 1)">↓</button>
            </div>
          </template>
        </draggable>
      </div>
    </transition>
  </div>
</template>

<script>
import { computed, ref, watch } from 'vue';
import draggable from 'vuedraggable';
import TableScaleControl from '@/components/UI/TableScaleControl.vue';
import { COLUMN_IDS, COLUMN_LABELS } from '../downtimeState.mjs';
import { DOWNTIME_HIGHLIGHT_MODES, HIGHLIGHT_PALETTES } from '../downtimeOptions.mjs';

export default {
  name: 'DowntimesToolbar',
  components: { draggable, TableScaleControl },
  props: {
    currentPage: { type: Number, default: 1 },
    totalPages: { type: Number, default: 1 },
    totalItems: { type: Number, default: 0 },
    refreshing: { type: Boolean, default: false },
    exporting: { type: Boolean, default: false },
    filterOpen: { type: Boolean, default: false },
    sorting: { type: Array, default: () => [] },
    columnOrder: { type: Array, default: () => COLUMN_IDS },
    columnVisibility: { type: Object, default: () => ({}) },
    scale: { type: String, default: 'normal' },
    highlightMode: { type: String, default: 'none' },
    palette: { type: String, default: 'colorful' },
  },
  emits: ['pageChanged', 'refresh', 'export', 'toggleFilter', 'columnOrderChanged', 'columnVisibilityChanged', 'resetSettings', 'scaleChanged', 'highlightChanged'],
  setup(props, { emit }) {
    const settingsOpen = ref(false);
    const settingsOrder = ref([]);
    watch(() => props.columnOrder, () => {
      settingsOrder.value = props.columnOrder.map((id) => ({ id, label: COLUMN_LABELS[id] || id }));
    }, { immediate: true, deep: true });
    const visibleCount = computed(() => COLUMN_IDS.filter((id) => props.columnVisibility[id] !== false).length);
    const isVisible = (id) => props.columnVisibility[id] !== false;
    const isLastVisible = (id) => isVisible(id) && visibleCount.value === 1;
    const emitOrder = () => emit('columnOrderChanged', settingsOrder.value.map((item) => item.id));
    const move = (index, direction) => {
      const target = index + direction;
      if (target < 0 || target >= settingsOrder.value.length) return;
      const next = [...settingsOrder.value];
      [next[index], next[target]] = [next[target], next[index]];
      settingsOrder.value = next;
      emitOrder();
    };
    const toggleVisibility = (id) => {
      if (!isLastVisible(id)) emit('columnVisibilityChanged', { ...props.columnVisibility, [id]: !isVisible(id) });
    };
    const sortIndicator = (id) => {
      const index = props.sorting.findIndex((item) => item.id === id);
      return index < 0 ? '' : `${props.sorting[index].desc ? '↓' : '↑'}${index + 1}`;
    };
    return { settingsOpen, settingsOrder, visibleCount, isVisible, isLastVisible, emitOrder, move, toggleVisibility, sortIndicator, highlightModes: DOWNTIME_HIGHLIGHT_MODES, palettes: HIGHLIGHT_PALETTES };
  },
};
</script>

<style scoped>
.toolbar { position: relative; display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 8px; padding: 8px 12px; border-bottom: 1px solid #e5e7eb; }
.toolbar-left, .toolbar-right { display: flex; align-items: center; flex-wrap: wrap; gap: 8px; }
.total { padding-left: 4px; color: #667085; font-size: 13px; white-space: nowrap; }
.toolbar-btn { height: 30px; padding: 4px 12px; border: 1px solid #007bff; border-radius: 4px; background: #fff; color: #007bff; font-size: 13px; cursor: pointer; white-space: nowrap; user-select: none; }
.toolbar-btn:hover:not(:disabled), .toolbar-btn.active { background: #007bff; color: #fff; }
.toolbar-btn:disabled { cursor: not-allowed; opacity: .55; }
.settings-toggle { display: inline-flex; align-items: center; gap: 8px; }
.chevron { width: 7px; height: 7px; border-right: 2px solid currentColor; border-bottom: 2px solid currentColor; transform: translateY(-2px) rotate(45deg); transition: transform 160ms ease; }
.chevron.open { transform: translateY(2px) rotate(225deg); }
.settings-panel { flex: 0 0 100%; box-sizing: border-box; padding: 12px; border: 1px solid #d9e0e8; border-radius: 6px; background: #f8fafc; }
.settings-heading { display: flex; justify-content: space-between; align-items: flex-start; gap: 12px; margin-bottom: 10px; color: #344054; }
.settings-hint { margin-top: 2px; color: #667085; font-size: 12px; }
.settings-actions { display: flex; gap: 8px; }
.settings-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(230px, 1fr)); gap: 6px; }
.settings-row { display: flex; align-items: center; gap: 6px; min-width: 0; min-height: 36px; padding: 3px 6px; border: 1px solid #e1e6ed; border-radius: 5px; background: #fff; }
.drag-handle, .move-btn { display: inline-flex; align-items: center; justify-content: center; width: 28px; height: 28px; padding: 0; border: 0; background: transparent; color: #667085; cursor: pointer; }
.drag-handle { cursor: grab; }
.drag-grip { width: 12px; height: 18px; background-image: radial-gradient(circle, currentColor 1.4px, transparent 1.6px); background-size: 6px 6px; }
.move-btn { border: 1px solid #d0d5dd; border-radius: 4px; background: #fff; }
.move-btn:disabled { cursor: default; opacity: .35; }
.visibility-label { display: flex; align-items: center; gap: 6px; flex: 1; min-width: 0; cursor: pointer; user-select: none; }
.drag-handle, .move-btn { user-select: none; }
.visibility-label span { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.sort-indicator { width: 26px; color: #007bff; font-weight: 700; }
.hl-settings-row { display: flex; align-items: center; gap: 8px; margin-bottom: 10px; }
.settings-label { font-size: 13px; color: #344054; white-space: nowrap; }
.hl-select { height: 30px; padding: 4px 8px; border: 1px solid #ccc; border-radius: 4px; font-size: 13px; background: #fff; color: #333; box-sizing: border-box; }
.settings-accordion-enter-active, .settings-accordion-leave-active { transition: opacity 140ms ease, transform 140ms ease; }
.settings-accordion-enter-from, .settings-accordion-leave-to { opacity: 0; transform: translateY(-4px); }
@media (max-width: 900px) { .toolbar-left, .toolbar-right { width: 100%; } .settings-heading { flex-direction: column; } }

@media screen and (max-width: 769px) {
  .toolbar-right {
    flex-wrap: nowrap;
    overflow-x: scroll;
  }
}
</style>
